import { useState, type KeyboardEvent } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Send, Plus, MessageCircleQuestion } from 'lucide-react'
import { usePropertyAskSessions, usePropertyAskSession } from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { formatLocaleDateTime } from '@/lib/format-utils'
import { AskSourceCitations, type AskSource } from './ask-source-citation'

// Single-field schema for the question textarea. Required-ness mirrors the
// previous manual `if (!question.trim())` guard in handleAsk - there's no
// visible label/error chrome for this field (it never rendered one), so the
// message text is only used internally by the resolver.
const askSchema = z.object({
  question: z.string().trim().min(1, 'Question is required'),
})
type AskFormValues = z.infer<typeof askSchema>

interface AskSession {
  id: string
  title?: string | null
  updated_at?: string | null
}

interface AskMessage {
  id: string
  // Contractually `string` on the real response, though in practice only
  // 'user'/'assistant' values are ever produced. Downstream checks below
  // (`message.role === 'assistant'`) work identically against the wider type.
  role: string
  content: string
  sources?: AskSource[]
}

interface AskTabProps {
  propertyId: string
  isActive: boolean
}

/**
 * Ask-a-question tab for the property detail page. Answers are grounded in
 * this property's ingested knowledge (guest messages for now — more sources
 * land here over time). Sessions persist so a question can be continued later.
 */
export function AskTab({ propertyId, isActive }: AskTabProps) {
  const { toast } = useToast()
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const form = useForm<AskFormValues>({
    resolver: zodResolver(askSchema),
    defaultValues: { question: '' },
  })
  const question = useWatch({ control: form.control, name: 'question' })
  const { ref: questionFieldRef, ...questionFieldRest } = form.register('question')
  const [isAsking, setIsAsking] = useState(false)

  const {
    data: sessionsData,
    isLoading: isLoadingSessions,
    error: sessionsError,
    refetch: refetchSessions,
  } = usePropertyAskSessions(propertyId, { enabled: !!propertyId && isActive })

  const {
    data: sessionData,
    isLoading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = usePropertyAskSession(propertyId, selectedSessionId, {
    enabled: !!propertyId && isActive && !!selectedSessionId,
  })

  const sessions: AskSession[] = sessionsData?.sessions || []
  const messages: AskMessage[] = sessionData?.messages || []

  const handleNewSession = () => {
    setSelectedSessionId(null)
    form.reset({ question: '' })
  }

  // Called via `form.handleSubmit(onSubmit)` - the resolver's
  // `.trim().min(1)` stands in for the old `if (!question.trim())` guard,
  // so `data.question` arrives here already trimmed and non-empty.
  const onSubmit = async (data: AskFormValues) => {
    if (isAsking) return
    const trimmed = data.question

    setIsAsking(true)
    try {
      const result = await api.askPropertyQuestion(propertyId, {
        question: trimmed,
        sessionId: selectedSessionId ?? undefined,
      })
      form.reset({ question: '' })
      await refetchSessions()
      if (result.sessionId === selectedSessionId) {
        await refetchMessages()
      } else {
        setSelectedSessionId(result.sessionId)
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Failed to get an answer',
        description: message || 'Something went wrong asking this question.',
        variant: 'destructive',
      })
    } finally {
      setIsAsking(false)
    }
  }

  // form.handleSubmit(onSubmit) reads internal refs, so it's wrapped here
  // and invoked from event handlers rather than called inline during render.
  const handleAsk = () => {
    void form.handleSubmit(onSubmit)()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAsk()
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      {/* Sessions Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Sessions</CardTitle>
            <Button variant="ghost" size="sm" onClick={handleNewSession}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>Past questions for this property</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingSessions ? (
            <div className="space-y-2">
              <LoadingSkeleton className="h-12 w-full" />
              <LoadingSkeleton className="h-12 w-full" />
            </div>
          ) : sessionsError ? (
            <ErrorState error={sessionsError} onRetry={refetchSessions} />
          ) : sessions.length === 0 ? (
            <p className="text-sm text-textMuted">No questions asked yet.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {sessions.map(session => (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => setSelectedSessionId(session.id)}
                  className={`w-full rounded-card border p-3 text-left transition-all ${
                    selectedSessionId === session.id
                      ? 'border-mint bg-mint/5'
                      : 'border-border bg-surface2 hover:bg-surface2/80'
                  }`}
                >
                  <p className="text-sm text-text truncate">{session.title}</p>
                  <p className="mt-1 text-xs text-textMuted">
                    {formatLocaleDateTime(session.updated_at, { fallback: null })}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chat Section */}
      <Card>
        <CardHeader>
          <CardTitle>Ask</CardTitle>
          <CardDescription>
            Answers come from this property&apos;s guest message history.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {!selectedSessionId ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageCircleQuestion className="mb-4 h-12 w-12 text-textFaint" />
              <p className="text-sm text-textMuted">
                Ask something like &quot;what&apos;s the lock code&quot; or &quot;how many beach
                chairs do we have&quot;.
              </p>
            </div>
          ) : isLoadingMessages ? (
            <div className="space-y-3">
              <LoadingSkeleton className="h-16 w-full" />
              <LoadingSkeleton className="h-16 w-full" />
            </div>
          ) : messagesError ? (
            <ErrorState error={messagesError} onRetry={refetchMessages} />
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`rounded-card border p-3 ${
                    message.role === 'assistant'
                      ? 'border-mint/30 bg-mint/5 ml-6'
                      : 'border-border bg-surface2 mr-6'
                  }`}
                >
                  <p className="text-sm text-text whitespace-pre-wrap wrap-break-word">
                    {message.content}
                  </p>
                  {message.role === 'assistant' &&
                    message.sources &&
                    message.sources.length > 0 && (
                      <AskSourceCitations propertyId={propertyId} sources={message.sources} />
                    )}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2 pt-2 border-t border-border">
            <textarea
              {...questionFieldRest}
              ref={questionFieldRef}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about this property..."
              className="flex-1 min-h-[60px] rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text transition-all placeholder:text-textFaint focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              disabled={isAsking}
            />
            <Button onClick={handleAsk} disabled={isAsking || !question.trim()}>
              {isAsking ? (
                'Asking...'
              ) : (
                <>
                  <Send className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
